/**
 * Demo Data Service
 * Powers the demo-org "Generate demo data" flow: ask the AI for a preview batch of
 * realistic dummy submissions, eyeball them, then commit the previewed rows as-is.
 * Mirrors excelImportService (same api client + endpoint constants).
 */

import axiosInstance, { apiRequest } from '../axiosInstance';
import { DEMO_DATA_ENDPOINTS } from '../endpoints';

const demoDataService = {
  /**
   * Generate a preview batch of demo rows (one AI call, writes nothing).
   * @param {string} entityType - 'forms' | 'workorders' | 'customers'
   * @param {number} targetId - form schema id for 'forms'; demo org id for 'workorders'/'customers'
   * @param {Object} request - { recordCount, dateFrom, dateTo, tone, seed, fieldOverrides }
   * @returns {Promise<Object>} { aiEnriched, requestedCount, rows }
   */
  previewDemoData: async (entityType, targetId, request) => {
    return apiRequest(() =>
      axiosInstance.post(DEMO_DATA_ENDPOINTS.PREVIEW(entityType, targetId), request)
    );
  },

  /**
   * Commit a previewed batch of demo rows. Post back the exact rows array from previewDemoData, unchanged.
   * @param {string} entityType - 'forms' | 'workorders' | 'customers'
   * @param {number} targetId - form schema id for 'forms'; demo org id for 'workorders'/'customers'
   * @param {Array} rows - The DemoRowDto[] from the preview response
   * @param {string[]} [aiImageFieldKeys] - Image/Drawing field keys to generate with AI (else placeholder SVGs)
   * @returns {Promise<Object>} { created, skipped, errors }
   */
  commitDemoData: async (entityType, targetId, rows, aiImageFieldKeys) => {
    return apiRequest(() =>
      axiosInstance.post(DEMO_DATA_ENDPOINTS.COMMIT(entityType, targetId), {
        rows,
        aiImageFieldKeys: aiImageFieldKeys?.length ? aiImageFieldKeys : null,
      })
    );
  },

  /**
   * AI scaffold step 1 (SuperAdmin): plan a set of forms for a demo org from one prompt (one AI call).
   * @param {number} orgId
   * @param {{prompt?: string, formCount?: number}} request - blank prompt ⇒ server derives it from the org name
   * @returns {Promise<{tone: string, forms: Array<{name: string, description: string}>}>}
   */
  scaffoldPlan: async (orgId, request = {}) => {
    return apiRequest(() => axiosInstance.post(DEMO_DATA_ENDPOINTS.SCAFFOLD_PLAN(orgId), request));
  },

  /**
   * AI scaffold step 2 (SuperAdmin): generate + persist ONE planned form into the demo org (one AI call).
   * @param {number} orgId
   * @param {{name?: string, description: string}} request
   * @returns {Promise<{formSchemaId: number, name: string, fieldCount: number}>}
   */
  scaffoldForm: async (orgId, request) => {
    return apiRequest(() => axiosInstance.post(DEMO_DATA_ENDPOINTS.SCAFFOLD_FORM(orgId), request));
  },

  /**
   * The demo org's fillable forms with submission counts — the scaffold dialog's fill targets (SuperAdmin).
   * @param {number} orgId
   * @returns {Promise<Array<{id: number, name: string, submissionCount: number}>>}
   */
  listDemoForms: async (orgId) => {
    return apiRequest(() => axiosInstance.get(DEMO_DATA_ENDPOINTS.ORG_FORMS(orgId)));
  },

  /**
   * AI-fill the New/Edit Demo config form: one prompt (or the typed name) → suggested demo name, URL slug,
   * and landing headline/subheadline (SuperAdmin, one AI call, no org needed).
   * @param {string} prompt
   * @returns {Promise<{name: string, slug: string, headline: string, subheadline: string}>}
   */
  suggestDemoConfig: async (prompt) => {
    return apiRequest(() => axiosInstance.post(DEMO_DATA_ENDPOINTS.SUGGEST_CONFIG, { prompt }));
  },

  /**
   * Generate the shared men/women avatar pool reused across all demos. NON-BLOCKING — generation runs in the
   * background (a few paid image calls, ~minutes), so this returns immediately; poll getAvatarPoolStatus until
   * ready. `append=false` is idempotent (no-op if the pool exists); `append=true` always generates + appends
   * another batch (the manage window's "Generate more"). SuperAdmin only.
   * @param {boolean} [append=false]
   * @returns {Promise<Object>} { ready, generating, count, message }
   */
  ensureAvatarPool: async (append = false) => {
    const url = append ? `${DEMO_DATA_ENDPOINTS.ENSURE_AVATAR_POOL}?append=true` : DEMO_DATA_ENDPOINTS.ENSURE_AVATAR_POOL;
    return apiRequest(() => axiosInstance.post(url));
  },

  /**
   * Generate a batch of avatars into the shared pool with a custom prompt / count / image model, appended to the
   * pool. NON-BLOCKING — poll getAvatarPoolStatus until `generating` is false. SuperAdmin only.
   * @param {{prompt?:string, count?:number, model?:string}} opts
   * @returns {Promise<Object>} { ready, generating, count, message }
   */
  generateAvatars: async ({ prompt, count, model } = {}) => {
    return apiRequest(() =>
      axiosInstance.post(DEMO_DATA_ENDPOINTS.AVATAR_POOL_GENERATE, {
        prompt: prompt?.trim() || null,
        count: count || null,
        model: model || null,
      })
    );
  },

  /**
   * Current avatar-pool status, polled while background generation runs.
   * @returns {Promise<Object>} { ready, generating, count }
   */
  getAvatarPoolStatus: async () => {
    return apiRequest(() => axiosInstance.get(DEMO_DATA_ENDPOINTS.AVATAR_POOL_STATUS));
  },

  /**
   * List the avatars currently in the shared pool (gallery tiles with presigned thumbnail URLs). SuperAdmin.
   * @returns {Promise<Array<{name:string,url:string,sizeBytes:number,uploadedAt:string}>>}
   */
  listAvatars: async () => {
    return apiRequest(() => axiosInstance.get(DEMO_DATA_ENDPOINTS.AVATAR_POOL_LIST));
  },

  /**
   * Upload one image into the shared avatar pool. SuperAdmin.
   * @param {File} file
   * @returns {Promise<{name:string,url:string,sizeBytes:number,uploadedAt:string}>}
   */
  addAvatar: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    // Override the axios default application/json, else the FormData is JSON-serialized and the file never
    // arrives (same gotcha as userService.uploadAvatar / excelImportService).
    return apiRequest(() =>
      axiosInstance.post(DEMO_DATA_ENDPOINTS.AVATAR_POOL_ADD, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    );
  },

  /**
   * Delete one avatar from the shared pool by its filename. SuperAdmin.
   * @param {string} name
   */
  deleteAvatar: async (name) => {
    return apiRequest(() => axiosInstance.delete(DEMO_DATA_ENDPOINTS.AVATAR_POOL_DELETE(name)));
  },
};

export default demoDataService;
